import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Automotive Industry Focus page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **Why this cut has a page of its own.** `focus()` in `content/reports.ts`
 * writes every Industry Focus entry from one sentence template, and Automotive
 * was one of the seventeen that carried it: a two-paragraph series description
 * under an amber note saying the sector's own words had never been supplied. They
 * were supplied on 2026-08-27, *"automotive focus report"*, with the same four
 * attachments the Airlines cut arrived with the day before — a full-page
 * screenshot of digitopia.co's Automotive Industry Focus page as the reference,
 * the cover at 2560px, Figure 1 and the report mockup. So this is the seventh
 * designed report page and the second focus cut to arrive with **both** of its
 * figures as files.
 *
 * **Where the words come from, band by band.** Everything here is transcribed
 * from the reference screenshot, which is a 2880×9112 full-page capture rendered
 * down to read: the *shape* of the page is certain and the *wording* is a careful
 * reading of body copy set at a handful of pixels. Every band that carries prose
 * therefore carries a `check`, exactly as `content/daimiAirports.ts` and
 * `content/daimiAirlines.ts` do, and `daimiAutomotiveChecks()` feeds them into the
 * same manifest as the rest of the site's amber notes so a production build
 * cannot ship them unread.
 *
 * **What the live page has that this page cannot.** Its five Download buttons
 * open an embedded HubSpot form. These documents are prerendered with no client
 * bundle at all (see `src/prerender.tsx`), so an embedded form script would never
 * run — and the other thirty reports on this site hand the reader the PDF from
 * our own bucket instead. So every Download button here is that PDF, and
 * `DAIMI_AU_DOWNLOAD.check` is what keeps the question of gating open.
 */

/** The report this page belongs to — the branch `pages/Reports.tsx` takes on it. */
export const DAIMI_AU_SLUG = 'digital-and-ai-maturity-report-automotive-2026'

/**
 * The hero's three paragraphs, read off the reference screenshot.
 *
 * The third is `FOCUS_CLOSE` in `reports.ts` word for word — the series' own
 * closing line, which that module took from the live Airlines page in the first
 * place, and which the live Automotive page closes its hero with too. Written out
 * rather than imported so that rewording the series line for the other sixteen
 * cuts cannot silently reword the page it appears on.
 */
export const DAIMI_AU_INTRO = {
  body: [
    'The automotive industry leads in digital and AI maturity by leveraging its strength in large-scale execution and value chain management to drive real impact.',
    'Future gains depend on scaling proven solutions and modernizing core systems, not on launching more pilots. The key risk is execution strain from limited resources, AI FOMO, and cultural resistance.',
    'This report outlines the sector’s maturity, leading strategies, and actions to accelerate transformation.',
  ],
  /**
   * The report cover. It already existed as a 400px square — the size every card
   * in the reports grid carries — and the file attached with this request is the
   * same artwork at 2560px, so it replaced it under the same key rather than
   * landing beside it under a second name. The index card reads the same path and
   * simply gets a sharper cover out of it.
   */
  photo: {
    src: '/photo/daimi-report-2026-automotive-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Automotive Focus, February 2026',
  } satisfies Photo,
  check:
    'One reading still wanted: the three paragraphs are transcribed from the reference screenshot rather than supplied as text — the second one in particular, which names the three risks. (The cover beside them is no longer a question. The first capture of this page was one 2880×9112 image in which the hero read as empty on the right, so this note asked whether it was meant to be; the slices re-sent on 2026-08-28 show the cover there, which is where it is here.)',
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The three bullets are one block on the live page in which each line opens with
 * a bolded label — "Core Strengths:", "The Scaling Gap:", "Industrializing AI:".
 * Split into `label` and `body` here so the label can be set rather than
 * emphasised inside a sentence, which is what a `<strong>` in the middle of a
 * paragraph gives.
 */
export const DAIMI_AU_FINDINGS = {
  heading: 'DAIMI Findings: Precision vs. Scalability',
  lede: 'Automotive performance shows an operationally robust sector where a gap is widening between steady performers and breakaway leaders.',
  points: [
    {
      label: 'Core Strengths',
      body: 'Maturity peaks in measurable, repeatable areas like manufacturing, supply chain, and quality management.',
    },
    {
      label: 'The Scaling Gap',
      body: 'Progress is limited not by ambition, but by the difficulty of scaling across legacy systems and diverse plant environments.',
    },
    {
      label: 'Industrializing AI',
      body: 'Leaders integrate digital and AI directly into their operating models, while others remain stalled in isolated pilots.',
    },
  ],
  /**
   * Figure 1 itself, which is the file supplied with this request — and it is a
   * different chart from the one the Airlines cut carries. Not a vitality
   * scatter: six years of DAIMI results, 2020 to 2025, each year a range from the
   * laggard through the average to the leader, with the company count for that
   * year in the header. It is 1200×638 on a white ground, so it gets a white card
   * of its own and a floor under it — six stacked ranges with three labelled
   * values each do not survive being squeezed to phone width.
   */
  photo: {
    src: '/photo/daimi-automotive-study-results.webp',
    alt: 'DAIMI study results for the automotive industry, 2020 to 2025: the laggard, average and leader score each year, rising from 1.77–3.20 in 2020 to 2.60–3.98 in 2025',
  } satisfies Photo,
  /**
   * The caption, punctuated as the live page punctuates it — a colon rather than
   * an em dash, and the serial comma before "and Laggards". Read off the re-sent
   * reference slices on 2026-08-28, where it is legible; the first capture of
   * this page was one 2880×9112 image in which it was not.
   */
  caption:
    'Figure 1: Digital & AI Maturity Index (DAIMI) Study Results: Automotive Industry Score, Averages, Leaders, and Laggards',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
  check:
    'Figure 1 is the file you supplied and is shown whole, including its own year-by-year company counts (3 in 2020 rising to 76 in 2025). What wants a reading against digitopia.co is the prose: the lede and the three findings are transcribed from the full-page screenshot, where body copy renders a few pixels tall. The caption is transcribed from the same capture.',
}

/** The analysis band, with the report mockup beside it. */
export const DAIMI_AU_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Automotive Industry',
  /**
   * One paragraph, not two. It was split after "competitive advantage" until
   * 2026-08-28; the re-sent reference slices show the live band running all three
   * sentences together as a single block, so the break came out.
   */
  body: [
    'To lead in 2026, automotive executives must move beyond the “pilot trap” and embed AI into the fabric of their operations to bridge the gap between steady performance and breakaway leadership. Success requires a disciplined focus on modernizing core platforms and upskilling teams to turn execution strain into a sustained competitive advantage. Download the full report to access the detailed DAIMI benchmarks and the strategic roadmap for your organization.',
  ],
  /** The mockup supplied with this request — the automotive cover, not another cut's. */
  photo: {
    src: '/photo/daimi-report-2026-automotive-mockup.webp',
    alt: 'Two copies of the Digital & AI Maturity Report 2026 Automotive Focus',
  } satisfies Photo,
  check:
    'This paragraph is transcribed from the reference screenshot rather than supplied as text, so it wants reading against the live page once before production — the opening sentence in particular, which is the page’s argument in one sentence.',
}

/*
 * The methodology and global-perspective bands used to be written out here, each
 * with its own transcription of copy that is the same on every focus cut. They
 * are `content/reportClosing.ts` now, drawn by `components/ReportClosing.tsx` on
 * every report page but three — asked for on 2026-08-28, *"her report sayfamızın
 * en altında bu tasarım bire bir aynı şekilde olmalı"*. The assessment-count
 * question those notes raised is asked once there instead of once per cut.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_AU_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Automotive Industry Focus cut of the Digital & AI Maturity Report 2026.',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy
 * checks in — the same mechanism `daimiChecks()` and `daimiAirlinesChecks()` use,
 * and appended to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file — this cut
 * arrived with both of its figures. Three of the notes are a transcription nobody
 * has read against the original, one is a conflict between two numbers this site
 * states in two places, and one is a decision about gating that belongs to
 * marketing.
 */
export function daimiAutomotiveChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: DAIMI_AU_INTRO.check },
    { section: 'findings', check: DAIMI_AU_FINDINGS.check },
    { section: 'analysis', check: DAIMI_AU_ANALYSIS.check },
    { section: 'download', check: DAIMI_AU_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_AU_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
